import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import Loader from "../dist/Loader";
import { formatTimeTo12Hour, formatExcelTime,formatDateTimeToIst } from "../utils/formatTime";
import { formatDate, formatDateTime, setExcelDateCell } from "../utils/formatDate";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
const VisitSupport = () => {
  const location = useLocation();

  const today = new Date().toISOString().split("T")[0];
  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  const startDateStr = startOfMonth.toISOString().split("T")[0];

  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [fromDate, setFromDate] = useState(startDateStr);
  const [showTable, setShowTable] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [prevDateHeaders, setPrevDateHeaders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const tableWrapperRef = useRef(null);

  // ─── Track whether initial location.state has been consumed ───
  const locationStateConsumed = useRef(false);

  // ─── Utility ──────────────────────────────────────────────────
  const formatDateRangeHeaders = (startDate, endDate) => {
    const end = new Date(endDate);
    const day = end.getDate().toString().padStart(2, "0");
    const monthName = end.toLocaleString("default", { month: "long" });
    const year = end.getFullYear();
    const endFormatted = `${day} ${monthName} ${year}`;
    return [
      `${endFormatted} Calls`,
      `${endFormatted} Prod Calls`,
      `${endFormatted} First Call`,
      `${endFormatted} Last Call`,
      `${endFormatted} POB`,
    ];
  };
  const [toDate, setToDate] = useState(
    new Date(Date.now() - 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0]
  );
  // ─── Fetch employees (stable reference) ───────────────────────
  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    try {
      const params = [];
      if (stateId && stateId !== "All")
        params.push(`state_id=${encodeURIComponent(stateId)}`);
      if (divisionName && divisionName !== "All")
        params.push(`division=${encodeURIComponent(divisionName)}`);

      const url =
        `${BASE_URL}/api/employees` +
        (params.length ? `?${params.join("&")}` : "");

      const res = await axios.get(url, { headers: getAuthHeaders() });
      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...(res.data || []),
      ]);
    } catch {
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
    }
  }, []);

  // ─── Fetch report data (pure function, no side-effects on state) ──
  const fetchReportData = useCallback(
    async ({ state, division, divisionList, employee, from, to }) => {
      const headers = formatDateRangeHeaders(from, to);
      setPrevDateHeaders(headers);

      let url = `${BASE_URL}/api/combined-retailers/daily-visit-summary?start_date=${from}&end_date=${to}`;

      if (state && state !== "All") url += `&state_id=${state}`;

      if (division && division !== "All") {
        const found = divisionList.find(
          (d) => String(d.id) === String(division)
        );
        if (found) url += `&division=${encodeURIComponent(found.name)}`;
      }

      if (employee && employee !== "All") url += `&employee_id=${employee}`;

      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data?.results ?? [];
    },
    []
  );

  // ─── Initial data load (states + employees) ── runs once ──────
  useEffect(() => {
    const init = async () => {
      try {
        const [statesRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
        ]);
        setStates(statesRes.data || []);
      } catch {
        /* ignore */
      }
      await fetchEmployees("All", "All");
    };
    init();
  }, [fetchEmployees]);

  // ─── Restore from location.state (only once) ─────────────────
  useEffect(() => {
    if (!location.state || locationStateConsumed.current) return;
    locationStateConsumed.current = true;

    if (location.state.fromDocChem) {
      const { filters, tableState, options } = location.state;

      setSelectedState(filters?.selectedState ?? "All");
      setSelectedDivision(filters?.selectedDivision ?? "All");
      setSelectedEmployee(filters?.selectedEmployee ?? "All");
      setFromDate(filters?.fromDate || startDateStr);
      setToDate(filters?.toDate || today);
      setStates(options?.states || []);
      setDivisions(options?.divisions || []);
      setEmployees(options?.employees || []);

      if (tableState?.showTable && tableState?.reportData?.length) {
        setReportData(tableState.reportData);
        setShowTable(true);
        // Restore headers for the restored date range
        setPrevDateHeaders(
          formatDateRangeHeaders(
            filters?.fromDate || startDateStr,
            filters?.toDate || today
          )
        );
      }
    }

    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty – run once on mount

  // ─── State change handler ─────────────────────────────────────
  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");

    try {
      const url =
        `${BASE_URL}/api/divisions` +
        (stateId && stateId !== "All"
          ? `?state_id=${encodeURIComponent(stateId)}`
          : "");
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setDivisions(res.data || []);
    } catch {
      setDivisions([]);
    }

    fetchEmployees(stateId || "All", "All");
  };

  // ─── Division change handler ──────────────────────────────────
  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");

    try {
      const params = [];
      if (selectedState && selectedState !== "All")
        params.push(`state_id=${encodeURIComponent(selectedState)}`);

      if (divisionId && divisionId !== "All") {
        const division = divisions.find(
          (d) => String(d.id) === String(divisionId)
        );
        if (division)
          params.push(`division=${encodeURIComponent(division.name)}`);
      }

      const url =
        `${BASE_URL}/api/employees` +
        (params.length ? `?${params.join("&")}` : "");

      const res = await axios.get(url, { headers: getAuthHeaders() });
      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...(res.data || []),
      ]);
    } catch {
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
    }
  };

  // ─── GO button handler (single source of truth for API call) ─
  const handleGoClick = useCallback(async () => {
    if (new Date(fromDate) > new Date(toDate)) {
      toast.warning("Start date cannot be after end date");
      return;
    }

    setIsLoading(true);
    try {
      const results = await fetchReportData({
        state: selectedState,
        division: selectedDivision,
        divisionList: divisions,
        employee: selectedEmployee,
        from: fromDate,
        to: toDate,
      });
      setReportData(results);
      setShowTable(true);
    } catch {
      toast.error("Failed to load report");
      setReportData([]);
      setShowTable(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedState,
    selectedDivision,
    selectedEmployee,
    fromDate,
    toDate,
    divisions,
    fetchReportData,
  ]);

  // ─── Helpers ──────────────────────────────────────────────────
  const openDcrPreviewWindow = (url) => {
    window.open(url, "_blank", "width=1200,height=700,scrollbars=yes,resizable=yes");
  };

  // ─── Print ────────────────────────────────────────────────────
  const printableStyles = `
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial; font-size:12px; color:#111; }
      table { border-collapse: collapse; width: 100%; table-layout: fixed; word-break: break-word; }
      th, td { border: 1px solid #999; padding: 6px 8px; vertical-align: middle; }
      thead th { background: #2b6cb0; color: #fff; font-weight: 600; text-align: center; }
      .text-center { text-align: center; }
      .text-left { text-align: left; }
      .nowrap { white-space: nowrap; }
      .small { font-size: 11px; }
      a { color: #1d4ed8; text-decoration: underline; }
    </style>
  `;

  const getPrintableTableHtml = () => {
    if (!tableWrapperRef.current) return "<div>No table found</div>";
    const wrapper = tableWrapperRef.current.cloneNode(true);
    wrapper.querySelectorAll("button").forEach((btn) => {
      const span = document.createElement("span");
      span.innerHTML = btn.innerHTML;
      btn.parentNode.replaceChild(span, btn);
    });
    wrapper.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href");
      if (href) a.insertAdjacentText("afterend", ` (${href})`);
    });
    return wrapper.innerHTML;
  };

  const handlePrint = () => {
    try {
      const tableHtml = getPrintableTableHtml();
      const w = window.open("", "_blank", "width=1200,height=900");
      if (!w) {
        toast.warning("Please allow popups to enable printing.");
        return;
      }
      w.document.open();
      w.document.write(
        `<html><head><title>Visit/Support Report - Print</title>${printableStyles}</head><body>`
      );
      w.document.write(
        `<div style="margin:12px 8px;"><h2 style="margin-bottom:8px;text-align:center">Visit/Support Report</h2>`
      );
      w.document.write(tableHtml);
      w.document.write(
        `<div style="text-align:center;margin-top:12px;font-size:12px">Generated on: ${new Date().toLocaleString()}</div>`
      );
      w.document.write("</div></body></html>");
      w.document.close();
      w.focus();
      setTimeout(() => {
        try {
          w.print();
        } catch (_) { }
      }, 300);
    } catch {
      toast.error("Unable to print.");
    }
  };

  // ─── Excel Export (with live formulas + styling) ──────────────
  const handleExportExcel = async () => {
    if (!reportData.length) {
      toast.info("No data to export");
      return;
    }

    setIsLoading(true);

    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = "VisitSupport App";
      wb.created = new Date();
      const ws = wb.addWorksheet("Visit Support Report");

      // ── Style constants ──────────────────────────────────────────
      const THIN_BORDER = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
      const BLUE_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
      const GREEN_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FF22C55E" } };
      const ALT_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F4FF" } };
      const WHITE_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };

      // ── Column definitions ────────────────────────────────────────
      const staticLeft = [
        "S.No", "Emp ID", "Employee", "State", "HQ",
        "Desi", "Division", "DOJ", "Immediate Manager", "Leave",
      ];
      const staticRight = [
        "Total POB", "No of Customer", "Total Visits", "Call Avg", "JW",
      ];

      const dynCount = prevDateHeaders.length;
      const headers = [...staticLeft, ...prevDateHeaders, ...staticRight];
      const totalCols = headers.length;

      // ── Column indices ────────────────────────────────────────────
      const IDX = {
        SNO: 1,
        EMP_ID: 2,
        EMPLOYEE: 3,
        STATE: 4,
        HQ: 5,
        DESIG: 6,
        DIVISION: 7,
        DOJ: 8,
        MANAGER: 9,
        LEAVE: 10,
        CALLS: 10 + 1,
        PROD_CALLS: 10 + 2,
        FIRST_CALL: 10 + 3,
        LAST_CALL: 10 + 4,
        DATE_POB: 10 + 5,
        POB_TOTAL: 10 + dynCount + 1,
        CUSTOMERS: 10 + dynCount + 2,
        VISITS: 10 + dynCount + 3,
        CALL_AVG: 10 + dynCount + 4,
        JW: 10 + dynCount + 5,
      };

      // ── Column letter helper ──────────────────────────────────────
      const colLetter = (n) => {
        let letter = "";
        while (n > 0) {
          const rem = (n - 1) % 26;
          letter = String.fromCharCode(65 + rem) + letter;
          n = Math.floor((n - 1) / 26);
        }
        return letter;
      };

      const L = {};
      Object.entries(IDX).forEach(([key, col]) => {
        L[key] = colLetter(col);
      });

      // ── Column widths ─────────────────────────────────────────────
      const staticLeftWidths = [5, 10, 28, 14, 14, 12, 14, 12, 22, 12];
      const dynWidths = Array(dynCount).fill(12);
      const staticRightWidths = [16, 16, 14, 12, 7];
      const widths = [...staticLeftWidths, ...dynWidths, ...staticRightWidths];
      widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });

      // ── Header row ───────────────────────────────────────────────
      const headerRow = ws.addRow(headers);
      headerRow.height = 32;
      for (let c = 1; c <= totalCols; c++) {
        const cell = headerRow.getCell(c);
        cell.value = headers[c - 1];
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, name: "Calibri", size: 10 };
        cell.fill = BLUE_FILL;
        cell.border = THIN_BORDER;
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      }

      // ── Safe time helper ─────────────────────────────────────────
      const safeTime = (val) => {
        if (!val) return "";
        const str = String(val).trim();
        if (!str || str === "null" || str === "undefined" || str === "0") return "";
        if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(str)) return str;
        const match = str.match(/^(\d{1,2}):(\d{2})/);
        if (!match) return str;
        let h = parseInt(match[1], 10);
        const mn = match[2];
        const ap = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12;
        return `${String(h).padStart(2, "0")}:${mn} ${ap}`;
      };

      // ── Data rows ────────────────────────────────────────────────
      reportData.forEach((row, i) => {
        const excelR = i + 2;
        const isAlt = i % 2 !== 0;
        const isJW = Boolean(row.is_joint_working_manager ?? false);
        const base = isAlt ? ALT_FILL : WHITE_FILL;

        // ── Parse & round all numerics — no decimals ─────────────
        const callsNum = Math.round(Number(row.calls) || 0);
        const prodCallsNum = Math.round(Number(row.prod_calls) || 0);
        const datePobNum = Math.round(Number(row.pob_total) || 0);
        const pobTotalNum = Math.round(Number(row.pob) || 0);
        const custNum = Math.round(Number(row.no_of_customers) || 0);
        const visitsNum = Math.round(Number(row.total_customer_visits) || 0);
        const jwNum = Math.round(Number(row.jw) || 0);
        const callAvgNum = row.call_avg
          ? Math.round(parseFloat(row.call_avg))
          : 0;

        const firstCallVal = formatExcelTime(
  formatDateTimeToIst(row.first_call, true)
);
        const lastCallVal = formatExcelTime(row.last_call);
        const empRaw = String(row.user_id ?? "").trim();
        const empIsNum = /^\d+$/.test(empRaw);
        const empIDValue = empIsNum ? Number(empRaw) : empRaw;

        // ── Value map ─────────────────────────────────────────────
        const vals = {
          [IDX.SNO]: i + 1,
          [IDX.EMP_ID]: empIDValue,
          [IDX.EMPLOYEE]: String(row.employee || ""),
          [IDX.STATE]: String(row.state || ""),
          [IDX.HQ]: String(row.hq || ""),
          [IDX.DESIG]: String(row.designation || ""),
          [IDX.DIVISION]: String(row.division || ""),
          [IDX.DOJ]: row.doj || null,
          [IDX.MANAGER]: String(row.manager || ""),
          [IDX.LEAVE]: String(row.leave_type || ""),
          [IDX.CALLS]: callsNum,
          [IDX.PROD_CALLS]: prodCallsNum,
          // ✅ AFTER
          [IDX.FIRST_CALL]: firstCallVal,
          [IDX.LAST_CALL]: lastCallVal,
          [IDX.DATE_POB]: datePobNum,
          [IDX.POB_TOTAL]: pobTotalNum,
          [IDX.CUSTOMERS]: custNum,
          [IDX.VISITS]: visitsNum,
          [IDX.CALL_AVG]: callAvgNum,
          [IDX.JW]: jwNum,
        };

        const excelRow = ws.getRow(excelR);
        excelRow.height = 22;

        for (let c = 1; c <= totalCols; c++) {
          const cell = excelRow.getCell(c);
          const val = vals[c] ?? "";

          // ── Cell value + format ───────────────────────────────
          if (c === IDX.FIRST_CALL || c === IDX.LAST_CALL) {
            cell.value = val ?? null;
            cell.numFmt = "hh:mm AM/PM";

          } else if (c === IDX.EMP_ID) {
            cell.value = val;
            cell.numFmt = empIsNum ? "0" : "@";

          } else if (c === IDX.DOJ) {
            setExcelDateCell(cell, val, "dd-mmm-yyyy");

          } else {
            cell.value = val;

            if (typeof val === "number") {
              cell.numFmt = "0";
            }
          }

          // ── Common styling ───────────────────────────────────
          cell.border = THIN_BORDER;
          cell.font = { name: "Calibri", size: 10 };
          cell.alignment = {
            vertical: "middle",
            horizontal: typeof val === "number" ? "center" : "left",
            wrapText: false,
          };

          // ── JW green highlight ───────────────────────────────
          if (c === IDX.JW && isJW) {
            cell.fill = GREEN_FILL;
            cell.font = { name: "Calibri", size: 10, bold: true, color: { argb: "FFFFFFFF" } };
            cell.alignment = { vertical: "middle", horizontal: "center" };
          } else {
            cell.fill = base;
          }
        }

        excelRow.commit();
      });

      // ── Sheet settings ────────────────────────────────────────────
      ws.views = [{ state: "frozen", ySplit: 1 }];
      ws.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: totalCols },
      };

      // ── Save ──────────────────────────────────────────────────────
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `VisitSupportReport_${toDate}.xlsx`
      );
      toast.success("Export successful!");

    } catch (err) {
      console.error("Export error:", err);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  // ─── Render ───────────────────────────────────────────────────
  return (
    <div className="p-4 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}

      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 ml-14 mb-5">
          <span className="text-blue-700">Previous </span>
          <span className="text-blue-500">Working</span>
        </h1>

        <div className="flex flex-col items-center gap-3">
          <div className="grid grid-cols-2 gap-x-36 gap-y-3">
            {/* State */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap w-36 text-right">
                State
              </label>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-xs w-80"
                value={selectedState}
                onChange={(e) => handleStateChange(e.target.value)}
              >
                <option value="All">All</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Division */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap w-24 text-right">
                Division
              </label>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-xs w-80"
                value={selectedDivision}
                onChange={(e) => handleDivisionChange(e.target.value)}
              >
                <option value="All">All</option>
                {divisions.map((div) => (
                  <option key={div.id} value={div.id}>
                    {div.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Employee */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap w-36 text-right">
                Employee
              </label>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-xs w-80"
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
              >
                {employees.map((emp) =>
                  emp.id === "All" ? (
                    <option key="All" value="All">
                      All
                    </option>
                  ) : (
                    <option key={emp.id} value={emp.id}>
                      {`${emp.first_name} ${emp.last_name} ${emp.employee} (${emp.designation} | ${emp.headquarter_name} | ${emp.division} | ${emp.state_name})`}
                    </option>
                  )
                )}
              </select>
            </div>

            {/* Dates */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap w-24 text-right">
                Start-Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-1 py-1 border border-gray-300 rounded text-xs w-[108px]"
              />
              <label className="text-xs text-gray-600 whitespace-nowrap">
                End-Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-1 py-1 border border-gray-300 rounded text-xs w-[108px]"
              />
            </div>
          </div>

          <CustomButton variant="go" onClick={handleGoClick} disabled={isLoading}>
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>
      </div>

      {showTable && (
        <>
          <div className="visit-table-section mt-5 border border-gray-300 rounded bg-white p-4">
            <div className="w-full rounded border bg-white">
              <div ref={tableWrapperRef} className="border rounded shadow table-wrapper">
                <table className="unlock-dcr-leave-table text-sm">
                  <colgroup>
                    <col style={{ width: 48 }} />
                    <col style={{ width: 72 }} />
                    <col style={{ width: 220 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 96 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 96 }} />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 76 }} />
                    <col style={{ width: 96 }} />
                    <col style={{ width: 96 }} />
                    <col style={{ width: 96 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 72 }} />
                    <col style={{ width: 72 }} />
                  </colgroup>

                  <thead className="bg-blue-500 text-white">
                    <tr>
                      {[
                        "S.No", "Emp ID", "State", "Employee",
                        "Desi", "HQ", "Division", "DOJ", "Immediate Manager", "Leave",
                      ].map((h) => (
                        <th
                          key={h}
                          className="bg-blue-600 text-white px-2 py-2 text-[11px] font-bold border text-left"
                        >
                          {h}
                        </th>
                      ))}

                      {prevDateHeaders.map((head, idx) => (
                        <th
                          key={idx}
                          className="bg-blue-600 text-white px-2 py-2 text-[11px] font-bold border break-words"
                        >
                          {head}
                        </th>
                      ))}

                      {["Total POB", "No of\nCustomer", "Total\nVisits", "Call Avg", "JW"].map(
                        (h) => (
                          <th
                            key={h}
                            className="bg-blue-600 text-white px-2 py-2 text-[11px] font-bold border"
                          >
                            {h.split("\n").map((line, i) => (
                              <React.Fragment key={i}>
                                {line}
                                {i === 0 && h.includes("\n") && <br />}
                              </React.Fragment>
                            ))}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {reportData.length > 0 ? (
                      reportData.map((row, index) => (
                        <tr
                          key={index}
                          className="odd:bg-white even:bg-gray-50 hover:bg-blue-50 transition-colors"
                        >
                          <td className="px-2 py-2 border text-left">{index + 1}</td>
                          <td className="px-2 py-2 border">{row.user_id}</td>
                          <td className="px-2 py-2 border">{row.state}</td>
                          <td className="px-2 py-2 border text-left">{row.employee}</td>
                          <td className="px-2 py-2 border">{row.designation}</td>
                          <td className="px-2 py-2 border">{row.hq}</td>
                          <td className="px-2 py-2 border">{row.division}</td>
                          <td className="px-2 py-2 border">{formatDate(row.doj)}</td>
                          <td className="px-2 py-2 border">{row.manager}</td>
                          <td className="px-2 py-2 border">{row.leave_type || ""}</td>

                          {/* Calls — clickable */}
                          <td
                            className="px-2 py-2 border text-blue-600 underline cursor-pointer"
                            onClick={() =>
                              openDcrPreviewWindow(
                                `/admin/dcrpreviewtable?employee_id=${row.emp_id}&date=${toDate}`
                              )
                            }
                          >
                            {row.calls}
                          </td>

                          <td className="px-2 py-2 border">{row.prod_calls}</td>
                          <td className="px-2 py-2 border">{formatDateTimeToIst(row.first_call, true)}</td>
                          <td className="px-2 py-2 border">{formatTimeTo12Hour(row.last_call)}</td>
                          <td className="px-2 py-2 border">{row.pob_total}</td>

                          {/* POB — clickable */}
                          <td
                            className="px-2 py-2 border text-blue-700 font-semibold underline cursor-pointer"
                            onClick={() =>
                              openDcrPreviewWindow(
                                `/admin/dcr/dcrmonthlyretailPOBInSlab?emp_code=${row.emp_id}&from_date=${fromDate}&to_date=${toDate}&emp_name=${encodeURIComponent(row.employee)}&CallType=Productive`
                              )
                            }
                          >
                            {row.pob}
                          </td>

                          <td className="px-2 py-2 border">{row.no_of_customers}</td>
                          <td className="px-2 py-2 border">{row.total_customer_visits}</td>
                          <td className="px-2 py-2 border">
                            {row.call_avg ? Math.round(parseFloat(row.call_avg)) : 0}
                          </td>
                          <td
                            className={`px-2 py-2 border font-bold text-center ${Boolean(row.is_joint_working_manager ?? false)
                              ? "bg-green-500 text-white"
                              : ""
                              }`}
                          >
                            {row.jw || 0}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={10 + prevDateHeaders.length + 5}
                          className="text-center py-4 text-gray-500"
                        >
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print" disabled={isLoading}>
              🖨
            </CustomButton>
            <CustomButton onClick={handleExportExcel} label="Export" variant="export" disabled={isLoading}>
              📥
            </CustomButton>
          </div>
        </>
      )}
    </div>
  );
};

export default VisitSupport;